import React, { useState } from "react";
import { MessageBar, MessageBarType, Toggle } from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import { setupCustomerIntegrationSetting } from "@/api/integrations";
import type { CustomerIntegrationSettingDto } from "@/api/integrations";

interface Props {
  integrationId: number;
  setting: CustomerIntegrationSettingDto;
  onSave?: () => void;
  onClose?: () => void;
}

const EditCustomerSettingForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const setting = props.setting;

  const formik = useFormik({
    initialValues: {
      usesMasterConfig: setting.usesMasterConfig,
      isActive: setting.isActive,
    },
    onSubmit: (values) => {
      setIsSaving(true);
      setErrorMessage("");
      setupCustomerIntegrationSetting({
        integrationId: props.integrationId,
        customerId: setting.customerId,
        usesMasterConfig: values.usesMasterConfig,
        isActive: values.isActive,
      })
        .then(
          () => {
            props.onSave?.();
          },
          (error) => {
            const data = error?.response?.data;
            setErrorMessage(
              data?.detail || "Failed to update customer setting."
            );
          }
        )
        .finally(() => setIsSaving(false));
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <CustomerComboBox
              selectedCustomerId={setting.customerId}
              onChange={() => {}}
              disabled
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Uses Master Config"
              checked={formik.values.usesMasterConfig}
              onChange={(_, checked) =>
                formik.setFieldValue("usesMasterConfig", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default EditCustomerSettingForm;
